import { useEffect, useMemo, useRef, useState } from 'react';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import Polygon, { fromExtent } from 'ol/geom/Polygon';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Fill, Icon, Stroke, Style, Text } from 'ol/style';

import { findPlan } from '@utils/api/find';
import { SIMILARITY_Z_INDEX } from '@utils/map/similarity/similaritySource';
import { withAlpha } from '@utils/map/similarity/similarityStyle';

import { planErrorBlocks, polygonM2 } from './findGeometry';
import { LAYOUT as PANEL } from './FindPanel';
import { TILE_STATE } from './searchState';

// Wait for the map to settle before asking where the next search would run
const PREVIEW_DEBOUNCE_MS = 350;
// A drawn area's plan that failed for another reason than the area (compute, the network) is asked
// once more after this; the view's is asked again when the map next moves
const PREVIEW_RETRY_MS = 2000;
// Label inset from the area's corner, in screen pixels
const LABEL_INSET = 6;
// Room the label keeps from the Find panel on the right, in screen pixels
const PANEL_CLEARANCE = PANEL.width + PANEL.right + 12;

/**
 * Where an area's label goes: its top-right corner (top centre for the view's own area), moved into
 * what the map shows when the corner is off screen or under the Find panel, so the label is never
 * hidden above the map or behind a panel.
 * @param {number[]} extent - The area's extent
 * @param {number[]|null} view - The map's visible extent, or null
 * @param {number} resolution - Map units per pixel
 * @param {boolean} centred
 * @returns {number[]} [x, y]
 */
export const labelAnchor = (extent, view, resolution, centred) => {
	const [minX, minY, maxX, maxY] = extent;
	let x = centred ? (minX + maxX) / 2 : maxX;
	let y = maxY;
	if (!view) return [x, y];
	const [vMinX, vMinY, vMaxX, vMaxY] = view;
	const right = vMaxX - PANEL_CLEARANCE * resolution;
	if (centred) {
		const left = Math.max(minX, vMinX);
		x = (left + Math.min(maxX, right > left ? right : vMaxX)) / 2;
	} else x = Math.max(Math.min(x, right), Math.max(minX, vMinX) + 200 * resolution);
	y = Math.max(Math.min(y, vMaxY), Math.min(minY, vMinY) + 40 * resolution);
	return [x, y];
};
// The squares (and their checkboxes) show only once a square is at least this wide on screen, in
// pixels: zoomed out they are noise, and the panel says how far a search has got
export const GRID_MIN_PX = 220;
// The squares' lines alone show from this width on screen, in pixels, so the grid stays visible
// before the checkboxes appear
export const GRID_LINE_MIN_PX = 48;
// While a search runs, squares smaller than this on screen are left out, except the one being searched
const SQUARE_MIN_PX = 4;
// The checkbox in a finished square's top-right corner, in screen pixels: it grows with the square
// (`fraction` of its width) between `min` and `max`, inset from the corner
export const CHECKBOX = { min: 18, max: 30, fraction: 0.06, inset: 8 };

/** The checkbox's side for a square this wide on screen, or 0 when the square is too small to show. */
export const checkboxSize = squarePx =>
	squarePx < GRID_MIN_PX
		? 0
		: Math.round(Math.min(CHECKBOX.max, Math.max(CHECKBOX.min, squarePx * CHECKBOX.fraction)));

/** The checkbox's centre, in screen pixels, from the pixel of its square's top-right corner. */
export const checkboxCentre = ([x, y], size) => [
	x - CHECKBOX.inset - size / 2,
	y + CHECKBOX.inset + size / 2,
];

const sameArea = (a, b) => Boolean(a && b) && a.every((v, k) => Math.abs(v - b[k]) < 1e-6);

/** "4.6 ha", "0.35 ha" or "850 m²": an area in square metres */
export const formatArea = m2 =>
	m2 < 1000 ? `${Math.round(m2)} m²` : `${(m2 / 10000).toFixed(m2 < 100000 ? 2 : 1)} ha`;

export { polygonM2 };

/** The size of a plan's area: the drawn polygon's when there is one, else the squares' */
export const hectares = (plan, polygon) =>
	formatArea(polygon ? polygonM2(polygon) : plan.area_m[0] * plan.area_m[1]);

/**
 * The area's size, on the map at its top-right corner: "3.95 ha · 5 × 4 squares of 44 m", or a drawn
 * area's "1.20 ha · 7 squares of 44 m"
 */
export const describeArea = (plan, polygon) => {
	if (!plan?.grid) return '';
	const squares = polygon ? `${plan.tiles.length}` : `${plan.grid[0]} × ${plan.grid[1]}`;
	return `${hectares(plan, polygon)} · ${squares} squares of ${Math.round(plan.tile_m)} m`;
};

/**
 * An extent snapped to the search grid: each edge moves to the nearest grid line, so the squares
 * taken are those whose centre lies inside it. At least the square under its centre.
 * @param {number[]} extent - EPSG:3857
 * @param {{ origin: number[], cell_units: number }} grid - From a plan
 * @returns {number[]} The snapped extent, on square boundaries
 */
export const snapToGrid = (extent, { origin: [ox, oy], cell_units: cell }) => {
	let [c0, c1] = [Math.round((extent[0] - ox) / cell), Math.round((extent[2] - ox) / cell)];
	let [r0, r1] = [Math.round((oy - extent[3]) / cell), Math.round((oy - extent[1]) / cell)];
	if (c1 <= c0) {
		c0 = Math.floor(((extent[0] + extent[2]) / 2 - ox) / cell);
		c1 = c0 + 1;
	}
	if (r1 <= r0) {
		r0 = Math.floor((oy - (extent[1] + extent[3]) / 2) / cell);
		r1 = r0 + 1;
	}
	return [ox + c0 * cell, oy - r1 * cell, ox + c1 * cell, oy - r0 * cell];
};

// The view's extent, snapped to the search grid once it is known
const viewBounds = (mapObject, grid) => {
	const view = mapObject.getView().calculateExtent(mapObject.getSize());
	return grid ? snapToGrid(view, grid) : view;
};

// One square per grid cell (not per tile: the tiles' overlap is how cells are merged, not shown), and
// the area: the drawn polygon, else the squares' extent
const planFeatures = (plan, polygon) => [
	...plan.tiles.map(tile => {
		const feature = new Feature(fromExtent(tile.core));
		feature.set('tile', tile.i);
		return feature;
	}),
	(() => {
		const area = new Feature(
			polygon ? new Polygon([polygon]) : fromExtent(plan.searched_bounds)
		);
		area.set('area', true);
		return area;
	})(),
];

// A checkbox image per state, colours and size, drawn once at the screen's pixel ratio: an empty box,
// a tick on green, or a cross on red
const checkboxes = new Map();
const checkboxImage = (mark, background, border, markColor, size) => {
	const key = [mark, background, border, markColor, size].join('|');
	if (checkboxes.has(key)) return checkboxes.get(key);
	const ratio = window.devicePixelRatio || 1;
	const line = Math.max(2, size / 10);
	const canvas = document.createElement('canvas');
	canvas.width = size * ratio;
	canvas.height = size * ratio;
	const ctx = canvas.getContext('2d');
	ctx.scale(ratio, ratio);
	ctx.beginPath();
	ctx.roundRect(line / 2, line / 2, size - line, size - line, size / 5);
	ctx.fillStyle = background;
	ctx.fill();
	ctx.lineWidth = line * 0.8;
	ctx.strokeStyle = border;
	ctx.stroke();
	ctx.lineWidth = line;
	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';
	ctx.strokeStyle = markColor;
	ctx.beginPath();
	if (mark === 'tick') {
		ctx.moveTo(size * 0.26, size * 0.52);
		ctx.lineTo(size * 0.43, size * 0.69);
		ctx.lineTo(size * 0.75, size * 0.33);
	} else if (mark === 'cross') {
		ctx.moveTo(size * 0.32, size * 0.32);
		ctx.lineTo(size * 0.68, size * 0.68);
		ctx.moveTo(size * 0.68, size * 0.32);
		ctx.lineTo(size * 0.32, size * 0.68);
	}
	ctx.stroke();
	const style = new Icon({ img: canvas, size: [canvas.width, canvas.height], scale: 1 / ratio });
	checkboxes.set(key, style);
	return style;
};

/**
 * Where a search runs, drawn on the map as the squares of the orthophoto's search grid:
 * - while the panel is open, the squares the next search would cover (dashed): those whose centre
 *   is on screen, or inside the drawn area;
 * - during and after a search, its squares: queued, running (accent), finished with a checkbox chip
 *   holding the count, ticked (green) or cleared (red).
 *
 * @param {Object} params
 * @param {Object|null} params.mapObject
 * @param {string} [params.projectUuid]
 * @param {Object} params.token - antd theme token
 * @param {boolean} params.enabled - Show the next search's area
 * @param {{ bounds: number[], polygon: number[][] }|null} params.area - A drawn area (its extent and
 *   ring, EPSG:3857), or null for the view
 * @param {Object|null} params.search - The current search's state (searchReducer)
 * @param {Object} [params.tileReview] - Plan tile index → 'ticked' or 'cleared'
 * @param {number|null} [params.selectedTile] - The square selected for review
 * @param {Object|null} [params.sizedFor] - What the next search looks for, `{ objects }` (words)
 *   or `{ groups }` (examples): its squares are about ten of them wide. Null: unsized squares
 * @returns {{ preview: Object|null, grid: Object|null, searchArea: Function }} The next search's plan
 *   (or `{ error, blocking }` when it failed; `blocking` when the area is the problem), the grid, and where the next search runs:
 *   `{ bounds, polygon }`
 */
export const useSearchGrid = ({
	mapObject,
	projectUuid,
	token,
	enabled,
	area,
	search,
	tileReview,
	selectedTile,
	sizedFor = null,
}) => {
	const [preview, setPreview] = useState(null);
	// The grid of the objects looked for (origin and square size), for snapping the view and a
	// drawing; another object has another grid
	const sizedKey = JSON.stringify(sizedFor);
	const sizedRef = useRef(sizedFor);
	sizedRef.current = sizedFor;
	const [sizedGrid, setGrid] = useState(null);
	const grid = sizedGrid?.key === sizedKey ? sizedGrid : null;
	const tilesRef = useRef({});
	const searchingRef = useRef(false);
	const reviewRef = useRef({ review: {}, selected: null });
	const plan = search?.plan ?? null;
	// The polygon the current search was drawn as, if it was
	const searchPolygon = search?.request?.polygon ?? null;

	// Where the next search runs: the drawn area, or the squares whose centre is on screen
	const searchArea = () => (area ? { ...area } : { bounds: viewBounds(mapObject, grid) });

	// Asked once per object looked for, with a one-square plan at the view's centre
	useEffect(() => {
		if (!mapObject || !enabled || !projectUuid || grid) return undefined;
		let alive = true;
		const [x, y] = mapObject.getView().getCenter();
		findPlan({ projectUuid, bounds: [x - 1, y - 1, x + 1, y + 1], ...sizedRef.current })
			.then(({ origin, cell_units: cellUnits }) => {
				if (alive) setGrid({ key: sizedKey, origin, cell_units: cellUnits });
			})
			.catch(() => {});
		return () => {
			alive = false;
		};
	}, [mapObject, projectUuid, enabled, grid, sizedKey]);

	// The next search's plan: the drawn area's, or the view's whenever it settles
	useEffect(() => {
		if (!mapObject || !enabled || !projectUuid) {
			setPreview(null);
			return undefined;
		}
		let timer;
		let latest = 0;
		let alive = true;
		let retried = false;
		const update = (delay = PREVIEW_DEBOUNCE_MS) => {
			clearTimeout(timer);
			timer = setTimeout(async () => {
				latest += 1;
				const mine = latest;
				// `blocking`: the area itself is the problem (too large, outside); else only the preview failed
				const next = await findPlan({
					projectUuid,
					...(area ?? { bounds: viewBounds(mapObject, grid) }),
					...sizedRef.current,
				}).catch(error => ({ error: error.message, blocking: planErrorBlocks(error) }));
				if (!alive || mine !== latest) return;
				setPreview(next);
				if (area && next.error && !next.blocking && !retried) {
					retried = true;
					update(PREVIEW_RETRY_MS);
				}
			}, delay);
		};
		const onMove = () => update();
		update();
		if (!area) mapObject.on('moveend', onMove);
		return () => {
			alive = false;
			clearTimeout(timer);
			mapObject.un('moveend', onMove);
		};
	}, [mapObject, projectUuid, enabled, area, grid, sizedKey]);

	const labelFont = `${token.fontWeightStrong} ${token.fontSizeSM}px ${token.fontFamily}`;
	const white = token.colorWhite;
	// Lines over the orthophoto stay readable on bright ground (roofs, snow) and dark ground (forest)
	// alike: a light line on a dark halo
	const shade = new Stroke({ color: withAlpha(token.colorBgMask, 0.7), width: 5 });
	const squareShade = new Style({
		stroke: new Stroke({ color: withAlpha(token.colorBgMask, 0.35), width: 3 }),
	});

	// The searched (or next) area: an accent outline on a dark halo, a faint accent tint inside, and
	// its size in a label chip at the top-right corner
	// The view's own area fills the map under the panels, so its label sits at the top centre instead
	const areaStyle = (feature, resolution, { dashed, label, centred = false }) => {
		const size = mapObject?.getSize();
		const view = size ? mapObject.getView().calculateExtent(size) : null;
		const anchor = labelAnchor(feature.getGeometry().getExtent(), view, resolution, centred);
		return [
			new Style({
				stroke: shade,
				fill: new Fill({ color: withAlpha(token.colorPrimary, 0.05) }),
			}),
			new Style({
				stroke: new Stroke({
					color: token.colorPrimary,
					width: 2.5,
					lineDash: dashed ? [10, 6] : undefined,
				}),
			}),
			new Style({
				geometry: new Point(anchor),
				text: new Text({
					text: label,
					font: labelFont,
					// Inside the corner, so the navbar or the screen edge never hides it
					textAlign: centred ? 'center' : 'right',
					textBaseline: 'top',
					offsetX: centred ? 0 : -LABEL_INSET - 8,
					offsetY: LABEL_INSET + 4,
					padding: [3, 8, 3, 8],
					fill: new Fill({ color: white }),
					backgroundFill: new Fill({ color: withAlpha(token.colorBgMask, 0.75) }),
					backgroundStroke: new Stroke({ color: token.colorPrimary, width: 1 }),
				}),
			}),
		];
	};

	// The current search's squares; restyled in place as their states change
	const searchLayer = useMemo(() => {
		if (!plan) return null;
		const layer = new VectorLayer({
			source: new VectorSource({ features: planFeatures(plan, searchPolygon) }),
			zIndex: SIMILARITY_Z_INDEX + 1,
		});
		layer.setStyle((feature, resolution) => {
			if (feature.get('area')) {
				return areaStyle(feature, resolution, {
					dashed: false,
					centred: !searchPolygon,
					label: `Searched · ${describeArea(plan, searchPolygon)}`,
				});
			}
			const [minX, , maxX, maxY] = feature.getGeometry().getExtent();
			const squarePx = (maxX - minX) / resolution;
			const index = feature.get('tile');
			const tile = tilesRef.current[index] ?? { state: TILE_STATE.QUEUED };
			const running = tile.state === TILE_STATE.RUNNING;
			const finished = tile.state === TILE_STATE.DONE || tile.state === TILE_STATE.CHECKED;
			if (squarePx < GRID_MIN_PX) {
				// Zoomed out: while a search runs, each square as a thin line and the one being searched lit,
				// so its progress shows over the map; afterwards thin lines while they are big enough to read
				if (!searchingRef.current && squarePx < GRID_LINE_MIN_PX) return null;
				if (squarePx < SQUARE_MIN_PX && !running) return null;
				return new Style({
					stroke: new Stroke({
						color: running
							? token.colorPrimary
							: withAlpha(white, finished ? 0.55 : 0.35),
						width: running ? 2 : 1,
						lineDash: finished || running ? undefined : [3, 3],
					}),
					fill: running
						? new Fill({ color: withAlpha(token.colorPrimary, 0.28) })
						: finished
							? new Fill({ color: withAlpha(white, 0.06) })
							: undefined,
				});
			}
			// A reviewed square: green when ticked, red when cleared
			const review = reviewRef.current.review[index];
			const reviewColor =
				review === 'ticked'
					? token.colorSuccess
					: review === 'cleared'
						? token.colorError
						: null;
			const styles = [
				squareShade,
				new Style({
					stroke: new Stroke({
						color: running
							? withAlpha(token.colorPrimary, 0.8)
							: reviewColor
								? withAlpha(reviewColor, 0.8)
								: withAlpha(white, finished ? 0.55 : 0.45),
						width: running || reviewColor ? 2 : 1.25,
						lineDash: finished || running || reviewColor ? undefined : [4, 4],
					}),
					fill: running
						? new Fill({ color: withAlpha(token.colorPrimary, 0.08) })
						: reviewColor
							? new Fill({ color: withAlpha(reviewColor, 0.07) })
							: undefined,
				}),
			];
			if (reviewRef.current.selected === index) {
				styles.unshift(
					new Style({ stroke: new Stroke({ color: token.colorBgMask, width: 6 }) }),
					new Style({ stroke: new Stroke({ color: white, width: 3 }) })
				);
			}
			// The checkbox, top right, sized with the square: empty to review, ticked (green), cleared (red)
			const size = checkboxSize(squarePx);
			if (finished && size) {
				const [mark, background, border] =
					review === 'ticked'
						? ['tick', token.colorSuccess, token.colorSuccess]
						: review === 'cleared'
							? ['cross', token.colorError, token.colorError]
							: ['none', withAlpha(token.colorBgBase, 0.45), withAlpha(white, 0.85)];
				const offset = (CHECKBOX.inset + size / 2) * resolution;
				styles.push(
					new Style({
						geometry: new Point([maxX - offset, maxY - offset]),
						image: checkboxImage(mark, background, border, white, size),
					})
				);
			}
			return styles;
		});
		return layer;
		// The style reads square states from tilesRef; token changes are rare
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plan, searchPolygon]);

	useEffect(() => {
		tilesRef.current = search?.tiles ?? {};
		searchingRef.current = Boolean(search?.running);
		reviewRef.current = { review: tileReview ?? {}, selected: selectedTile };
		searchLayer?.changed();
	}, [search?.tiles, search?.running, tileReview, selectedTile, searchLayer]);

	useEffect(() => {
		if (!mapObject || !searchLayer) return undefined;
		mapObject.addLayer(searchLayer);
		return () => mapObject.removeLayer(searchLayer);
	}, [mapObject, searchLayer]);

	// The next search's squares, dashed, unless a search is running or it covers the same squares
	const showPreview =
		enabled &&
		preview?.tiles &&
		!search?.running &&
		!sameArea(preview.searched_bounds, plan?.searched_bounds);
	const previewLayer = useMemo(() => {
		if (!showPreview) return null;
		const layer = new VectorLayer({
			source: new VectorSource({ features: planFeatures(preview, area?.polygon) }),
			zIndex: SIMILARITY_Z_INDEX + 1,
		});
		layer.setStyle((feature, resolution) => {
			if (feature.get('area')) {
				return areaStyle(feature, resolution, {
					dashed: true,
					centred: !area,
					label: `${area ? 'Drawn area' : 'This view'} · ${describeArea(preview, area?.polygon)}`,
				});
			}
			const [minX, , maxX] = feature.getGeometry().getExtent();
			if ((maxX - minX) / resolution < GRID_LINE_MIN_PX) return null;
			return [
				squareShade,
				new Style({
					stroke: new Stroke({
						color: withAlpha(white, 0.45),
						width: 1.25,
						lineDash: [4, 4],
					}),
				}),
			];
		});
		return layer;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPreview, preview, area]);

	useEffect(() => {
		if (!mapObject || !previewLayer) return undefined;
		mapObject.addLayer(previewLayer);
		return () => mapObject.removeLayer(previewLayer);
	}, [mapObject, previewLayer]);

	return { preview, grid, searchArea };
};
